<template>
  <div class="case-ledger p-4">
    <div class="flex justify-between items-center mb-4">
      <h3 class="text-lg font-semibold">Loan Ledger & Repayment History</h3>
      <v-btn small color="primary" outlined @click="fetchEmiDetails" :loading="loading">
        <v-icon left small>mdi-refresh</v-icon>
        Refresh Ledger
      </v-btn>
    </div>

    <div v-if="loading && !ledgerData.length" class="text-center py-12">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <div class="mt-2 text-sm text-gray-500">Fetching ledger data...</div>
    </div>

    <div v-else-if="!ledgerData.length" class="text-center py-8">
      <v-icon size="64" color="grey lighten-1">mdi-book-open-variant</v-icon>
      <div class="mt-4 text-gray-600">No ledger entries available for this loan.</div>
    </div>

    <div v-else>
      <!-- Ledger Summary Stats -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div class="p-4 bg-white border-l-4 border-blue-500 shadow-sm rounded-r-lg">
          <div class="text-xs font-bold text-gray-500 uppercase mb-1">Sanctioned</div>
          <div class="text-xl font-bold text-blue-700">₹{{ formatCurrency(totalPrincipal) }}</div>
        </div>
        <div class="p-4 bg-white border-l-4 border-green-500 shadow-sm rounded-r-lg">
          <div class="text-xs font-bold text-gray-500 uppercase mb-1">Total Paid</div>
          <div class="text-xl font-bold text-green-700">₹{{ formatCurrency(totalPaid) }}</div>
        </div>
        <div class="p-4 bg-white border-l-4 border-red-500 shadow-sm rounded-r-lg">
          <div class="text-xs font-bold text-gray-500 uppercase mb-1">Balance Due</div>
          <div class="text-xl font-bold text-red-700">₹{{ formatCurrency(totalBalance) }}</div>
        </div>
        <div class="p-4 bg-white border-l-4 border-orange-500 shadow-sm rounded-r-lg">
          <div class="text-xs font-bold text-gray-500 uppercase mb-1">Total Late Fees</div>
          <div class="text-xl font-bold text-orange-700">₹{{ formatCurrency(totalLateFees) }}</div>
        </div>
      </div>

      <v-card outlined class="mb-4 overflow-hidden">
        <v-card-title
          class="subtitle-2 font-weight-bold py-2 bg-gray-50 flex justify-between items-center"
        >
          <span>Transactions for Loan: {{ selectedCase.caseNo }}</span>
          <v-chip x-small color="blue" label text-color="white"
            >{{ ledgerData.length }} Entries</v-chip
          >
        </v-card-title>

        <v-simple-table fixed-header height="450px" class="ledger-table min-w-full">
          <template v-slot:default>
            <thead>
              <tr class="bg-gray-100">
                <th class="text-left text-xs font-bold uppercase py-3 px-4" style="width: 50px">
                  No.
                </th>
                <th
                  class="text-left text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 120px"
                >
                  Date
                </th>
                <th
                  class="text-left text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 180px"
                >
                  Description
                </th>
                <th
                  class="text-right text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 110px"
                >
                  Principal
                </th>
                <th
                  class="text-right text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 110px"
                >
                  Interest
                </th>
                <th
                  class="text-right text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 140px"
                >
                  Late Fee / Penalty
                </th>
                <th
                  class="text-right text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 110px"
                >
                  Total Due
                </th>
                <th
                  class="text-right text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 110px"
                >
                  Paid Amount
                </th>
                <th
                  class="text-left text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 100px"
                >
                  Status
                </th>
                <th
                  class="text-center text-xs font-bold uppercase py-3 px-4"
                  style="min-width: 120px"
                >
                  Overdue Days
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(emi, i) in ledgerData" :key="emi._id || i" :class="getRowClass(emi)">
                <td class="text-sm border-b px-4">{{ i + 1 }}</td>
                <td class="text-sm border-b px-4 whitespace-nowrap">
                  <div class="font-semibold">{{ formatDate(emi.dueDate) }}</div>
                  <div v-if="emi.paymentDate" class="text-xs text-green-600">
                    Paid: {{ formatDate(emi.paymentDate) }}
                  </div>
                </td>
                <td class="text-sm border-b px-4">
                  <div class="font-medium whitespace-nowrap">EMI #{{ i + 1 }} Repayment</div>
                  <div class="text-xs text-gray-500 truncate max-w-[150px]">
                    {{ emi.paymentMethod || 'Regular Payment' }}
                  </div>
                </td>
                <td class="text-right text-sm border-b px-4 tabular-nums whitespace-nowrap">
                  ₹{{ formatCurrency(emi.principalAmount) }}
                </td>
                <td class="text-right text-sm border-b px-4 tabular-nums whitespace-nowrap">
                  ₹{{ formatCurrency(emi.interestAmount) }}
                </td>
                <td class="text-right text-sm border-b px-4 tabular-nums whitespace-nowrap">
                  <div v-if="getOverdueInfo(emi).lateFeeAmount > 0" class="text-red-600 font-bold">
                    ₹{{ formatCurrency(getOverdueInfo(emi).lateFeeAmount) }}
                  </div>
                  <div v-else class="text-gray-400">₹0.00</div>
                  <div v-if="emi.penaltyAmount > 0" class="text-xs text-red-500">
                    + Penalty: ₹{{ formatCurrency(emi.penaltyAmount) }}
                  </div>
                </td>
                <td
                  class="text-right text-sm font-bold border-b px-4 tabular-nums whitespace-nowrap"
                >
                  ₹{{ formatCurrency(calculateTotalDue(emi)) }}
                </td>
                <td
                  class="text-right text-sm text-green-700 font-bold border-b px-4 tabular-nums whitespace-nowrap"
                >
                  ₹{{ formatCurrency(emi.paidAmount || (isPaid(emi) ? emi.emiAmount : 0)) }}
                </td>
                <td class="text-sm border-b px-4">
                  <v-chip
                    x-small
                    label
                    outlined
                    class="font-bold whitespace-nowrap"
                    :color="getStatusColor(emi.emiStatus)"
                  >
                    {{ emi.emiStatus }}
                  </v-chip>
                </td>
                <td class="text-center border-b px-4">
                  <div
                    v-if="getOverdueInfo(emi).isOverdue || getOverdueInfo(emi).isLatePaid"
                    class="flex flex-col items-center"
                  >
                    <span
                      :class="getOverdueInfo(emi).isLatePaid ? 'text-orange-600' : 'text-red-600'"
                      class="font-bold text-xs whitespace-nowrap"
                    >
                      {{ getOverdueInfo(emi).days }} Days
                    </span>
                    <span class="text-[10px] uppercase text-gray-500 whitespace-nowrap">
                      {{ getOverdueInfo(emi).isLatePaid ? 'Late' : 'Overdue' }}
                    </span>
                  </div>
                  <span v-else class="text-gray-300">-</span>
                </td>
              </tr>
            </tbody>
            <!-- Footer Summary -->
            <tfoot class="bg-gray-50 border-t-2">
              <tr class="font-bold">
                <td colspan="3" class="text-right py-3 px-4 whitespace-nowrap">
                  TOTAL REPAYMENT SUMMARY
                </td>
                <td class="text-right py-3 px-4 tabular-nums whitespace-nowrap">
                  ₹{{ formatCurrency(totalPrincipal) }}
                </td>
                <td class="text-right py-3 px-4 tabular-nums whitespace-nowrap">
                  ₹{{ formatCurrency(totalInterest) }}
                </td>
                <td class="text-right py-3 px-4 text-red-600 tabular-nums whitespace-nowrap">
                  ₹{{ formatCurrency(totalLateFees) }}
                </td>
                <td class="text-right py-3 px-4 tabular-nums whitespace-nowrap">
                  ₹{{ formatCurrency(totalDue) }}
                </td>
                <td class="text-right py-3 px-4 text-green-700 tabular-nums whitespace-nowrap">
                  ₹{{ formatCurrency(totalPaid) }}
                </td>
                <td colspan="2" class="bg-blue-50 text-center py-3 px-4">
                  <span class="text-xs text-blue-700 uppercase block whitespace-nowrap"
                    >Outs. Balance</span
                  >
                  <span class="text-blue-800 tabular-nums whitespace-nowrap"
                    >₹{{ formatCurrency(totalBalance) }}</span
                  >
                </td>
              </tr>
            </tfoot>
          </template>
        </v-simple-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import { calculateOverdueDays } from '@/utils/common'

export default {
  name: 'CaseLedger',
  props: {
    selectedCase: {
      type: Object,
      required: true
    },
    emiSchedule: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      loading: false,
      apiData: [],
      useApiData: false
    }
  },
  computed: {
    ledgerData() {
      const data = this.useApiData ? this.apiData : this.emiSchedule
      return (data || []).filter((emi) => String(emi.emiStatus || '').toLowerCase() !== 'pending')
    },
    totalPrincipal() {
      return this.ledgerData.reduce((sum, emi) => sum + (parseFloat(emi.principalAmount) || 0), 0)
    },
    totalInterest() {
      return this.ledgerData.reduce((sum, emi) => sum + (parseFloat(emi.interestAmount) || 0), 0)
    },
    totalLateFees() {
      return this.ledgerData.reduce((sum, emi) => {
        const overdue = this.getOverdueInfo(emi).lateFeeAmount || 0
        const penalty = parseFloat(emi.penaltyAmount) || 0
        return sum + overdue + penalty
      }, 0)
    },
    totalDue() {
      return this.ledgerData.reduce((sum, emi) => sum + this.calculateTotalDue(emi), 0)
    },
    totalPaid() {
      return this.ledgerData.reduce((sum, emi) => {
        const paid =
          parseFloat(emi.paidAmount) || (this.isPaid(emi) ? parseFloat(emi.emiAmount) : 0)
        return sum + paid
      }, 0)
    },
    totalBalance() {
      return Math.max(0, this.totalDue - this.totalPaid)
    }
  },
  methods: {
    async fetchEmiDetails() {
      if (!this.selectedCase?._id) return

      this.loading = true
      try {
        const params = {
          limit: 1000,
          customer: this.selectedCase.customer?._id
        }

        if (this.selectedCase.loanDetails?._id) {
          params.loan = this.selectedCase.loanDetails._id
        }

        const response = await this.$axios.get('/collection/emi-details', { params })

        if (response.data && response.data.emiDetails) {
          this.apiData = response.data.emiDetails.filter(
            (emi) =>
              emi.loan?.applicationNumber === this.selectedCase.caseNo ||
              emi.case?._id === this.selectedCase._id
          )
          this.useApiData = true
          this.$toast.success('Ledger updated with latest data')
        }
      } catch (error) {
        console.error('Error fetching EMI details for ledger:', error)
        this.$toast.error('Failed to fetch latest EMI details from API')
      } finally {
        this.loading = false
      }
    },
    formatDate(date) {
      return date ? this.$dayjs(date).format('DD MMM YYYY') : '-'
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value || 0)
    },
    getOverdueInfo(emi) {
      return calculateOverdueDays(
        emi.dueDate,
        emi.emiStatus,
        emi.paymentDate,
        emi.emiAmount,
        emi.lateFeeType,
        emi.lateFeeValue
      )
    },
    calculateTotalDue(emi) {
      const emiAmount = parseFloat(emi.emiAmount) || 0
      const lateFee = this.getOverdueInfo(emi).lateFeeAmount || 0
      const penalty = parseFloat(emi.penaltyAmount) || 0
      return emiAmount + lateFee + penalty
    },
    isPaid(emi) {
      const status = String(emi.emiStatus || '').toLowerCase()
      return status === 'paid'
    },
    getStatusColor(status) {
      const s = String(status || '').toLowerCase()
      if (s === 'paid') return 'green'
      if (s === 'overdue') return 'red'
      if (s === 'partial paid' || s === 'minimum amount paid') return 'orange'
      return 'blue'
    },
    getRowClass(emi) {
      const s = String(emi.emiStatus || '').toLowerCase()
      if (s === 'overdue') return 'bg-red-50/50'
      if (s === 'paid') return 'bg-green-50/20'
      return ''
    }
  }
}
</script>

<style scoped>
.ledger-table thead th {
  background-color: #f8f9fa !important;
  color: #37474f !important;
  border-bottom: 2px solid #cfd8dc !important;
  white-space: nowrap;
}

.ledger-table tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.ledger-table td {
  height: 60px !important;
  vertical-align: middle;
}

.tabular-nums {
  font-variant-numeric: tabular-nums;
}

/* Custom scrollbar for table */
.v-simple-table__wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.v-simple-table__wrapper::-webkit-scrollbar-thumb {
  background: #cfd8dc;
  border-radius: 10px;
}
.v-simple-table__wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
}

/* Sticky Footer */
.ledger-table :deep(.v-data-table__wrapper) {
  overflow-x: auto;
}

.ledger-table :deep(table) {
  border-collapse: separate;
  border-spacing: 0;
  padding: 0 !important;
}

.ledger-table :deep(tfoot) {
  position: sticky;
  bottom: 0;
  z-index: 10;
  background-color: #f8f9fa !important;
}

.ledger-table :deep(tfoot tr td) {
  border-top: 2px solid #cfd8dc !important;
  background-color: #f8f9fa !important;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
}

/* Ensure blue balance cell maintains its background when sticky */
.ledger-table :deep(tfoot tr td.bg-blue-50) {
  background-color: #eff6ff !important;
}
</style>
